import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types/entities'
import { getSessionConfig } from '@/lib/session-config'

interface AuthState {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    mfaRequired: boolean
    mfaMethods: string[]
    mfaNeedsSetup: boolean // Flag indicating if MFA secret needs to be set up
    mfaSetupToken: string | null // Short-lived enrollment token (proves password step); used only for TOTP setup
    /** Given by sign-in to a user with no sign-in device: good only for offering the one-time
     *  enrolment code an administrator issued (features/enrolment). Held in memory, never stored. */
    enrolmentToken: string | null
    /** After an enrolment code is accepted: the kind of first device being set up. */
    mfaEnrolFactor: 'totp' | 'webauthn' | null
    /** Short-lived challenge token (proves the password step). The ONLY way to reach the second
     *  factor: the passkey and TOTP endpoints accept nothing else, so neither is a sign-in on its
     *  own. Never a session token — the middleware confines it to those routes. */
    mfaToken: string | null
    // JWT Security state
    accessToken: string | null
    refreshToken: string | null
    /** The client's own estimate only. Never a reason to end the session: see checkSessionTimeout. */
    tokenExpiry: number | null
    lastTokenRefresh: number | null
    sessionId: string | null
    // Security monitoring
    securityEvents: any[]
    rateLimitStatus: {
        allowed: boolean
        remaining: number
        resetTime: string | null
    } | null
    // Session timeout warning
    sessionTimeoutWarning: {
        isActive: boolean
        timeRemaining: number
        warningShown: boolean
    }
}

interface AuthActions {
    setUser: (user: User | null) => void
    setLoading: (loading: boolean) => void
    setMfaRequired: (required: boolean, methods?: string[], needsSetup?: boolean, mfaSetupToken?: string | null, mfaToken?: string | null) => void
    setEnrolmentToken: (token: string | null) => void
    setMfaEnrolFactor: (factor: 'totp' | 'webauthn' | null) => void
    logout: () => void
    forceLogout: () => void
    clearMfa: () => void
    // JWT Security actions
    setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => void
    clearTokens: () => void
    updateTokenExpiry: (expiresIn: number) => void
    setSessionId: (sessionId: string) => void
    // Session timeout handling
    checkSessionTimeout: () => boolean
    checkSessionTimeoutWarning: () => boolean
    showSessionTimeoutWarning: (timeRemaining: number) => void
    hideSessionTimeoutWarning: () => void
    extendSession: () => void
    // Security monitoring actions
    setSecurityEvents: (events: any[]) => void
    setRateLimitStatus: (status: any) => void
    addSecurityEvent: (event: any) => void
    // Initialization
    initializeAuth: () => boolean
}

type AuthStore = AuthState & AuthActions


/**
 * Drop the persisted screen lock.
 *
 * Written directly rather than through the lock store so the two stores stay independent — auth
 * is cleared wholesale on logout and must not depend on another store's module having loaded.
 */
function clearLock() {
    try { localStorage.removeItem('vs-lock') } catch { /* private mode; the session still works */ }
}

export const useAuthStore = create<AuthStore>()(
    persist(
        (set, get) => ({
            // State
            user: null,
            isAuthenticated: false,
            isLoading: false,
            mfaRequired: false,
            mfaMethods: [],
            mfaNeedsSetup: false,
            mfaSetupToken: null,
            enrolmentToken: null,
            mfaEnrolFactor: null,
            mfaToken: null,
            // JWT Security state
            accessToken: null,
            refreshToken: null,
            tokenExpiry: null,
            lastTokenRefresh: null,
            sessionId: null,
            // Security monitoring
            securityEvents: [],
            rateLimitStatus: null,
            // Session timeout warning
            sessionTimeoutWarning: {
                isActive: false,
                timeRemaining: 0,
                warningShown: false,
            },

            // Actions
            setUser: (user) =>
                set((state) => ({
                    user,
                    // Set isAuthenticated to true if user exists (regardless of MFA state)
                    // The router will handle MFA redirection separately
                    isAuthenticated: !!user,
                    // Don't automatically clear MFA state - let it be set explicitly
                })),

            setLoading: (isLoading) => set({ isLoading }),

            setMfaRequired: (mfaRequired, mfaMethods = [], needsSetup = false, mfaSetupToken = null, mfaToken = null) =>
                set({ mfaRequired, mfaMethods, mfaNeedsSetup: needsSetup, mfaSetupToken, mfaToken, enrolmentToken: null, mfaEnrolFactor: null }),

            setEnrolmentToken: (enrolmentToken) => set({ enrolmentToken }),
            setMfaEnrolFactor: (mfaEnrolFactor) => set({ mfaEnrolFactor }),

            logout: () => {
                // The lock belonged to the session being ended; carrying it into the next one is
                // how somebody signs out, signs back in, and is met by a lock screen.
                clearLock()
                set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    mfaRequired: false,
                    mfaMethods: [],
                    mfaNeedsSetup: false,
                    mfaSetupToken: null,
                    enrolmentToken: null,
                    mfaEnrolFactor: null,
                    mfaToken: null,
                    accessToken: null,
                    refreshToken: null,
                    tokenExpiry: null,
                    lastTokenRefresh: null,
                    sessionId: null,
                    securityEvents: [],
                    rateLimitStatus: null,
                    sessionTimeoutWarning: {
                        isActive: false,
                        timeRemaining: 0,
                        warningShown: false,
                    },
                })
            },

            forceLogout: () => {
                // Force clear all state and redirect to login
                clearLock()
                set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    mfaRequired: false,
                    mfaMethods: [],
                    mfaNeedsSetup: false,
                    mfaSetupToken: null,
                    enrolmentToken: null,
                    mfaEnrolFactor: null,
                    mfaToken: null,
                    accessToken: null,
                    refreshToken: null,
                    tokenExpiry: null,
                    lastTokenRefresh: null,
                    sessionId: null,
                    securityEvents: [],
                    rateLimitStatus: null,
                    sessionTimeoutWarning: {
                        isActive: false,
                        timeRemaining: 0,
                        warningShown: false,
                    },
                })
                // Force redirect to login page - unless that is where we already are. Assigning the
                // address reloads the page even when it is the same one, so a request that fails on
                // the sign-in page itself would reload it, fail again, and reload it again (see
                // ScanStatusNotifications, 6 Oct 2026). Already there: the state is cleared, and
                // the form is on screen.
                if (window.location.pathname !== '/login') window.location.href = '/login'
            },

            clearMfa: () =>
                set({
                    mfaRequired: false,
                    mfaMethods: [],
                    mfaNeedsSetup: false,
                    mfaSetupToken: null,
                    enrolmentToken: null,
                    mfaEnrolFactor: null,
                    mfaToken: null,
                }),

            // JWT Security actions
            setTokens: (accessToken, refreshToken, expiresIn) =>
                set({
                    accessToken,
                    refreshToken,
                    tokenExpiry: Date.now() + (expiresIn * 1000),
                    lastTokenRefresh: Date.now(),
                    // Both step tokens are spent: a full session has replaced them.
                    mfaSetupToken: null,
                    enrolmentToken: null,
                    mfaEnrolFactor: null,
                    mfaToken: null,
                }),

            clearTokens: () =>
                set({
                    accessToken: null,
                    refreshToken: null,
                    tokenExpiry: null,
                    lastTokenRefresh: null,
                }),

            updateTokenExpiry: (expiresIn) =>
                set({
                    tokenExpiry: Date.now() + (expiresIn * 1000),
                    lastTokenRefresh: Date.now(),
                }),

            // Session timeout handling
            checkSessionTimeout: () => {
                const state = useAuthStore.getState()
                const now = Date.now()
                const config = getSessionConfig()

                // Check if we have isAuthenticated but no valid tokens (inconsistent state)
                if (state.isAuthenticated && (!state.accessToken || !state.refreshToken)) {
                    state.forceLogout()
                    return false
                }

                // NOT `tokenExpiry`. It is a guess stamped at sign-in (now + 15 minutes; the server's
                // access token lasts an hour, its refresh token a week) and slid only by mouse and
                // keyboard events in this tab, so a quarter of an hour spent in another window ended
                // a perfectly good session with no request made (14 Oct 2026). Whether a token is
                // still good is the server's answer — a 401 and then a refused refresh, in
                // lib/api/axios.ts — and how long someone may walk away is the administrator's idle
                // setting, kept by the router's idle timer.

                // Check if session has been idle too long
                if (state.lastTokenRefresh && (now - state.lastTokenRefresh) > config.sessionTimeoutMs) {
                    state.forceLogout()
                    return false
                }

                return true
            },

            checkSessionTimeoutWarning: () => {
                const state = useAuthStore.getState()
                const now = Date.now()
                const config = getSessionConfig()

                // Only check if user is authenticated and not already showing warning
                if (!state.isAuthenticated || state.sessionTimeoutWarning.warningShown) {
                    return false
                }

                // Check if we're within warning threshold of session timeout
                if (state.lastTokenRefresh) {
                    const timeUntilTimeout = config.sessionTimeoutMs - (now - state.lastTokenRefresh)

                    if (timeUntilTimeout <= config.warningThresholdMs && timeUntilTimeout > 0) {
                        // Show warning with remaining time
                        state.showSessionTimeoutWarning(Math.ceil(timeUntilTimeout / 1000))
                        return true
                    }
                }

                return false
            },

            showSessionTimeoutWarning: (timeRemaining) =>
                set(() => ({
                    sessionTimeoutWarning: {
                        isActive: true,
                        timeRemaining,
                        warningShown: true,
                    },
                })),

            hideSessionTimeoutWarning: () => {
                const current = get().sessionTimeoutWarning
                // Bail out if already hidden — avoids creating a new object on every
                // user-activity event (scroll, mousemove …) which would trigger
                // unnecessary re-renders across the entire component tree.
                if (!current.isActive && current.timeRemaining === 0 && !current.warningShown) {
                    return
                }
                set(() => ({
                    sessionTimeoutWarning: {
                        isActive: false,
                        timeRemaining: 0,
                        warningShown: false,
                    },
                }))
            },

            extendSession: () => {
                const state = get()
                const config = getSessionConfig()

                // Extend session by updating token expiry
                state.updateTokenExpiry(config.sessionExtensionSeconds)
                // Hide the warning
                state.hideSessionTimeoutWarning()

                // Add security event
                state.addSecurityEvent({
                    type: 'session_extended',
                    timestamp: new Date().toISOString(),
                    userAgent: navigator.userAgent,
                })
            },

            setSessionId: (sessionId) => set({ sessionId }),

            // Security monitoring actions
            setSecurityEvents: (events) => set({ securityEvents: events }),
            setRateLimitStatus: (status) => set({ rateLimitStatus: status }),
            addSecurityEvent: (event) =>
                set((state) => ({
                    securityEvents: [event, ...state.securityEvents].slice(0, 100), // Keep last 100 events
                })),

            // Initialize and validate authentication state
            initializeAuth: () => {
                const state = get()

                // If we have isAuthenticated but no valid tokens, clear the state
                // But don't clear if we're in MFA pending state
                if (state.isAuthenticated && (!state.accessToken || !state.refreshToken) && !state.mfaRequired) {
                    set({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                        mfaRequired: false,
                        mfaMethods: [],
                        accessToken: null,
                        refreshToken: null,
                        tokenExpiry: null,
                        lastTokenRefresh: null,
                        sessionId: null,
                        securityEvents: [],
                        rateLimitStatus: null,
                        sessionTimeoutWarning: {
                            isActive: false,
                            timeRemaining: 0,
                            warningShown: false,
                        },
                    })
                    return false
                }

                // Check session timeout
                return state.checkSessionTimeout()
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
                mfaRequired: state.mfaRequired,
                mfaMethods: state.mfaMethods,
                mfaSetupToken: state.mfaSetupToken,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                tokenExpiry: state.tokenExpiry,
                lastTokenRefresh: state.lastTokenRefresh,
                sessionId: state.sessionId,
            }),
        }
    )
)

// Selectors
export const useUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)
export const useIsLoading = () => useAuthStore((state) => state.isLoading)
export const useMfaRequired = () => useAuthStore((state) => state.mfaRequired)
export const useMfaMethods = () => useAuthStore((state) => state.mfaMethods)
export const useSessionTimeoutWarning = () => useAuthStore((state) => state.sessionTimeoutWarning)

// Helper functions
/**
 * TWO SHAPES OF `roles` REACH THIS STORE, and only one of them used to work.
 *
 *   /auth/login  ->  roles: ['admin']                    (strings)
 *   /auth/me     ->  roles: [{ name: 'admin', ... }]     (objects, with permissions)
 *
 * hasRole read role.name, which is undefined on a string, so every check returned false whenever
 * the login-shaped user was in the store. The sidebar's RECOVER and ADMIN sections are gated on
 * exactly these calls, so the menu rendered with only its OPERATE section — briefly after every
 * sign-in until /auth/me resolved and replaced the user, and permanently after a screen unlock,
 * which set the login-shaped user and had nothing to re-fetch.
 *
 * Both shapes are accepted here rather than only fixing the caller. A menu silently losing half
 * its items is too severe a failure for a difference in payload shape, and the two endpoints are
 * consumed in enough places that the next one to hit this would fail the same silent way. The
 * endpoints should still be reconciled — noted, not done here, because changing /auth/login's
 * response would ripple through consumers that expect strings.
 */
const roleNameOf = (role: unknown): string | null => {
    if (typeof role === 'string') return role
    if (role && typeof role === 'object' && 'name' in role) {
        const n = (role as { name?: unknown }).name
        return typeof n === 'string' ? n : null
    }
    return null
}

export const hasRole = (user: User | null, roleName: string): boolean => {
    return user?.roles?.some((role) => roleNameOf(role) === roleName) ?? false
}

/**
 * Permissions only ever arrive on the /auth/me shape — the login response carries none at all.
 * A string-roled user therefore has no permissions to check, which is correct: absence of
 * evidence, not a denial, and the caller should be reading the full user by then.
 */
export const hasPermission = (user: User | null, resource: string, action: string): boolean => {
    return user?.roles?.some((role) =>
        typeof role === 'object' && role !== null
        && (role as { permissions?: { resource: string; action: string }[] }).permissions?.some(
            (permission) => permission.resource === resource && permission.action === action,
        )
    ) ?? false
}








